import React, { useState } from 'react';
import { InputField } from '../../../components/uielements';
import {
  Heading,
  Main,
  SectionLeft,
  SignInSectionRight,
  FormWrapper,
  InputLabel,
  HelperText,
  AltText,
  ReestPasswordText,
  IconButton,
  StyledButton,
} from './login.style';
import { validateEmail, validatePassword } from '../../../helper/validationHelper';
import { useAuth } from '../../../hooks/useAuth';
import { getResetPasswordConfirmationCode, RestPassword } from '../../../services/auth';
import { LucideEye, LucideEyeOff } from 'lucide-react';

const AdminAuthPage = () => {
  const [formState, setFormState] = useState('sign-in');

  const handleSignIn = () => {
    setFormState('sign-in');
  };

  const handleResetPassword = () => {
    setFormState('password-reset');
  };

  return (
    <Main role="main">
      {formState === 'sign-in' ? (
        <SignIn handleResetPassword={handleResetPassword} />
      ) : (
        <PasswordReset handleSignIn={handleSignIn} />
      )}
    </Main>
  );
};

export default AdminAuthPage;

const SignIn = ({ handleResetPassword }) => {
  const { login } = useAuth();
  const [errorMessages, setErrorMessages] = useState({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setShowPassword((prevState) => !prevState);
  };

  const handleLogIn = async (event) => {
    try {
      setIsLoading(true);
      event.preventDefault();
      const data = new FormData(event.currentTarget);
      const isEmailValid = validateEmail(data.get('email'));
      const isPasswordValid = validatePassword(data.get('password'));

      const errors = {};

      if (!isEmailValid) {
        errors.email = 'Please enter a valid email address.';
      }
      if (!isPasswordValid) {
        errors.password = 'Please enter your password.';
      }

      if (Object.keys(errors).length > 0) {
        setErrorMessages(errors);
        setIsLoading(false);
        return;
      }

      await login(data.get('email'), data.get('password'), true);
    } catch (error) {
      setIsLoading(false);
      alert('Credentials not found. Please check your email and password.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setErrorMessages((prev) => ({
      ...prev,
      [name]: '',
    }));

    if (name === 'email' && value && !validateEmail(value)) {
      setErrorMessages((prev) => ({
        ...prev,
        email: 'Please enter a valid email address.',
      }));
    }
    if (name === 'password' && value && !validatePassword(value)) {
      setErrorMessages((prev) => ({
        ...prev,
        password: 'Password must be at least 6 characters.',
      }));
    }
  };

  return (
    <Main>
      <SectionLeft onSubmit={handleLogIn} role="form" aria-labelledby="signin-heading">
        <Heading id="signin-heading">Admin Log In</Heading>

        <div id="alert-container" role="alert" aria-live="polite"></div>

        <FormWrapper>
          <InputLabel htmlFor="signin-email">Email</InputLabel>
          <InputField
            type="email"
            id="signin-email"
            placeholder="Email"
            name="email"
            onChange={handleInputChange}
            aria-required="true"
            aria-invalid={!!errorMessages.email}
            aria-describedby={errorMessages.email ? 'email-error' : undefined}
          />
          {errorMessages.email && (
            <HelperText id="email-error" role="alert">
              {errorMessages.email}
            </HelperText>
          )}
        </FormWrapper>

        <FormWrapper style={{ marginBottom: '8px' }}>
          <InputLabel htmlFor="signin-password">Password</InputLabel>
          <InputField
            type={showPassword ? 'text' : 'password'}
            id="signin-password"
            placeholder="Password"
            name="password"
            onChange={handleInputChange}
            aria-required="true"
            aria-invalid={!!errorMessages.password}
            aria-describedby={errorMessages.password ? 'password-error' : undefined}
          />

          <IconButton
            onClick={(e) => togglePasswordVisibility(e)}
            role="button"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                togglePasswordVisibility(e);
              }
            }}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={0}
          >
            {showPassword ? (
              <LucideEye style={{ width: '19px', height: '19px', color: '#646465' }} />
            ) : (
              <LucideEyeOff style={{ width: '19px', height: '19px', color: '#646465' }} />
            )}
          </IconButton>

          {errorMessages.password && (
            <HelperText id="password-error" role="alert">
              {errorMessages.password}
            </HelperText>
          )}
        </FormWrapper>

        <ReestPasswordText
          onKeyPress={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleResetPassword();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Switch to password reset form"
          onClick={handleResetPassword}
        >
          Forgot your password?
        </ReestPasswordText>

        <StyledButton type="submit" loading={isLoading} aria-busy={isLoading}>
          {isLoading ? 'Logging in' : 'Log In'}
        </StyledButton>
      </SectionLeft>

      <SignInSectionRight aria-hidden="true" />
    </Main>
  );
};

const PasswordReset = ({ handleSignIn }) => {
  const [errorMessages, setErrorMessages] = useState({
    email: '',
    password: '',
    confirmationCode: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [getConfirmationCode, setGetConfirmationCode] = useState(false);
  const [email, setEmail] = useState('');

  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setShowPassword((prevState) => !prevState);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const data = new FormData(e.currentTarget);
    const emailValue = data.get('email');
    const password = data.get('password');
    const confirmationCode = data.get('confirmationCode');

    // Validate email
    if (!validateEmail(emailValue)) {
      setErrorMessages((prev) => ({
        ...prev,
        email: 'Please enter a valid email address.',
      }));
      setIsLoading(false);
      return;
    }

    try {
      if (!getConfirmationCode) {
        // Step 1: Request confirmation code
        const code = await getResetPasswordConfirmationCode(emailValue);
        if (code) {
          setEmail(emailValue);
          setGetConfirmationCode(true);
          alert('Confirmation code sent to your email. Please check your inbox.');
        }
      } else {
        // Step 2: Reset password with confirmation code
        if (!validatePassword(password)) {
          setErrorMessages((prev) => ({
            ...prev,
            password: 'Password must be at least 6 characters.',
          }));
          setIsLoading(false);
          return;
        }

        if (!confirmationCode) {
          setErrorMessages((prev) => ({
            ...prev,
            confirmationCode: 'Please enter the confirmation code.',
          }));
          setIsLoading(false);
          return;
        }

        await RestPassword(emailValue, password, confirmationCode);
        alert('Password reset successful. You can now log in with your new password.');
        setGetConfirmationCode(false);
        setErrorMessages({
          email: '',
          password: '',
          confirmationCode: '',
        });
        handleSignIn();
      }
    } catch (error) {
      setIsLoading(false);
      const errorMessage =
        error?.response?.data?.message || 'Oops, an error occurred. Please try again later.';
      alert(errorMessage);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setErrorMessages((prev) => ({
      ...prev,
      [name]: '',
    }));

    if (name === 'email' && value && !validateEmail(value)) {
      setErrorMessages((prev) => ({
        ...prev,
        email: 'Please enter a valid email address.',
      }));
    }

    if (name === 'password' && value && !validatePassword(value)) {
      setErrorMessages((prev) => ({
        ...prev,
        password: 'Password must be at least 6 characters.',
      }));
    }
  };

  const handleBackToSignIn = () => {
    setGetConfirmationCode(false);
    setErrorMessages({
      email: '',
      password: '',
      confirmationCode: '',
    });
    setEmail('');
    handleSignIn();
  };

  return (
    <Main>
      <SectionLeft
        onSubmit={handleResetPassword}
        role="form"
        aria-labelledby="password-reset-heading"
      >
        <Heading id="password-reset-heading">Forgot Password</Heading>

        <div id="alert-container" role="alert" aria-live="polite"></div>

        {!getConfirmationCode ? (
          <>
            {/* Step 1: Email Input */}
            <FormWrapper>
              <InputLabel htmlFor="reset-email">Email Address</InputLabel>
              <InputField
                type="email"
                id="reset-email"
                name="email"
                placeholder="Enter your registered email"
                onChange={handleInputChange}
                aria-required="true"
                aria-invalid={!!errorMessages.email}
                aria-describedby={errorMessages.email ? 'reset-email-error' : undefined}
                autoComplete="email"
              />
              {errorMessages.email && (
                <HelperText id="reset-email-error" role="alert">
                  {errorMessages.email}
                </HelperText>
              )}
            </FormWrapper>

            <AltText>We&lsquo;ll send a confirmation code to your email address.</AltText>
          </>
        ) : (
          <>
            {/* Step 2: Password & Confirmation Code */}
            <FormWrapper style={{ marginBottom: '12px' }}>
              <InputLabel htmlFor="reset-email-display">Email</InputLabel>
              <InputField
                type="email"
                id="reset-email-display"
                value={email}
                disabled
                aria-label="Your registered email"
              />
            </FormWrapper>

            <FormWrapper style={{ marginBottom: '8px' }}>
              <InputLabel htmlFor="new-password">New Password</InputLabel>
              <InputField
                type={showPassword ? 'text' : 'password'}
                id="new-password"
                name="password"
                placeholder="Enter your new password"
                onChange={handleInputChange}
                aria-required="true"
                aria-invalid={!!errorMessages.password}
                aria-describedby={errorMessages.password ? 'password-error' : undefined}
                autoComplete="new-password"
              />
              <IconButton
                onClick={(e) => togglePasswordVisibility(e)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    togglePasswordVisibility(e);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <LucideEye style={{ width: '19px', height: '19px', color: '#646465' }} />
                ) : (
                  <LucideEyeOff style={{ width: '19px', height: '19px', color: '#646465' }} />
                )}
              </IconButton>
              {errorMessages.password && (
                <HelperText id="password-error" role="alert">
                  {errorMessages.password}
                </HelperText>
              )}
            </FormWrapper>

            <FormWrapper>
              <InputLabel htmlFor="confirmation-code">Confirmation Code</InputLabel>
              <InputField
                type="text"
                id="confirmation-code"
                name="confirmationCode"
                placeholder="Enter the code sent to your email"
                onChange={handleInputChange}
                aria-required="true"
                aria-invalid={!!errorMessages.confirmationCode}
                aria-describedby={
                  errorMessages.confirmationCode ? 'confirmation-code-error' : undefined
                }
              />
              {errorMessages.confirmationCode && (
                <HelperText id="confirmation-code-error" role="alert">
                  {errorMessages.confirmationCode}
                </HelperText>
              )}
            </FormWrapper>

            <AltText>
              Check your email for the confirmation code. It may take a few moments to arrive.
            </AltText>
          </>
        )}

        <StyledButton type="submit" loading={isLoading} aria-busy={isLoading}>
          {getConfirmationCode ? 'Reset Password' : 'Get Code'}
        </StyledButton>

        <ReestPasswordText
          role="button"
          tabIndex={0}
          onClick={handleBackToSignIn}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleBackToSignIn();
            }
          }}
          aria-label="Return to the sign in page"
        >
          Take me back to the Sign In page, Click Here.
        </ReestPasswordText>
      </SectionLeft>

      <SignInSectionRight aria-hidden="true" />
    </Main>
  );
};
